import * as THREE from 'three';
import { ParticleEmitter } from "./particleemitter";
import { Effect, EffectParticle, EffectAnimation } from "./effect";
import { ShaderManager } from "../datamanagers/shadermanager";
import { TextureManager } from "../datamanagers/texturemanager";
import { ParticleSystemData } from '../loaders/ptl';
import { AnimationData } from '../loaders/zmo';
import { GeometryAnimator } from '../animators/geometryanimator';
import { ObjectAnimator } from '../animators/objectanimator';
import { MultiWait } from '../util/multiwait';
import { GDM } from '../helpers/datamanager';
import { EffectData } from '../loaders/eft';
import { Mesh } from '../loaders/zms';
import { convertZnzinBlendType, convertZnzinBlendOp } from '../loaders/rose';

/**
 * @constructor
 */
const EffectManager = function () {};

EffectManager._createEmitter = function (data: any) {
    let emitter = new ParticleEmitter();

    // Set initial particle properties
    emitter.alignType = data.alignType;
    emitter.coordType = data.coordType;
    emitter.emitRate = data.emitRate;
    emitter.emitRadius = data.emitRadius;
    emitter.gravity = data.gravity;
    emitter.lifeTime = data.lifeTime;
    emitter.loopCount = data.loopCount;
    emitter.particleCount = data.particleCount;
    emitter.spawnDirection = data.spawnDirection;
    emitter.spriteCols = data.spriteCols;
    emitter.spriteRows = data.spriteRows;

    // Load texture
    emitter.texture = TextureManager.load(data.texturePath);
    emitter.texture.repeat.set(1 / data.spriteCols, 1 / data.spriteRows);

    // Set material properties
    emitter.material = ShaderManager.get("particle").clone();
    emitter.material.uniforms = {
        texture1: { type: "t", value: null },
        uvScale: { type: "v2", value: new THREE.Vector2(1, 1) },
        uvOffset: { type: "v2", value: new THREE.Vector2(0, 0) },
        vColor: { type: "c", value: new THREE.Color(0, 0, 0) },
        vAlpha: { type: "f", value: 1 },
    };

    emitter.material.blending = THREE.CustomBlending;
    emitter.material.blendSrc = convertZnzinBlendType(data.blendSrc);
    emitter.material.blendDst = convertZnzinBlendType(data.blendDst);
    emitter.material.blendEquation = convertZnzinBlendOp(data.blendOp);

    // Set events
    for (let i = 0; i < data.events.length; ++i) {
        const eventData = data.events[i];
        const event: any = {
            time: eventData.time.getValueInRange(),
            type: eventData.type,
            fade: eventData.blended,
            fadeEvent: null,
        };

        switch (event.type) {
            case ParticleSystemData.EVENT_TYPE.SIZE:
                event.size = eventData.size;
                break;
            case ParticleSystemData.EVENT_TYPE.TIMER:
                event.timer = eventData.timer;
                break;
            case ParticleSystemData.EVENT_TYPE.RED:
                event.red = eventData.red;
                break;
            case ParticleSystemData.EVENT_TYPE.GREEN:
                event.green = eventData.green;
                break;
            case ParticleSystemData.EVENT_TYPE.BLUE:
                event.blue = eventData.blue;
                break;
            case ParticleSystemData.EVENT_TYPE.ALPHA:
                event.alpha = eventData.alpha;
                break;
            case ParticleSystemData.EVENT_TYPE.COLOR:
                event.color = eventData.color;
                break;
            case ParticleSystemData.EVENT_TYPE.VELOCITY_X:
                event.velocityX = eventData.velocityX;
                break;
            case ParticleSystemData.EVENT_TYPE.VELOCITY_Y:
                event.velocityY = eventData.velocityY;
                break;
            case ParticleSystemData.EVENT_TYPE.VELOCITY_Z:
                event.velocityZ = eventData.velocityZ;
                break;
            case ParticleSystemData.EVENT_TYPE.VELOCITY:
                event.velocity = eventData.velocity;
                break;
            case ParticleSystemData.EVENT_TYPE.TEXTURE:
                event.textureIndex = eventData.textureIndex;
                break;
            case ParticleSystemData.EVENT_TYPE.ROTATION:
                event.rotation = eventData.rotation;
                break;
            default:
                throw "Unexpected particle event type!";
        }

        emitter.events.push(event);
    }

    // Sort events
    emitter.events.sort(function (left: any, right: any) {
        return left.time - right.time;
    });

    // Link fade events
    for (let i = 0; i < emitter.events.length; ++i) {
        const event = emitter.events[i];

        for (let j = i + 1; j < emitter.events.length; ++j) {
            const other = emitter.events[j];

            if (other.fade && other.type === event.type) {
                event.fadeEvent = other;
                break;
            }
        }
    }

    return emitter;
};

EffectManager._loadParticleEffect = function (path: any, callback: any) {
    let effect = new EffectParticle();
    effect.path = path;

    ParticleSystemData.load(path, (particleSystem: any) => {
        for (let i = 0; i < particleSystem.emitters.length; ++i) {
            const emitter = EffectManager._createEmitter(
                particleSystem.emitters[i]
            );
            effect.emitters.push(emitter);

            if (effect.subRootObj) {
                effect.subRootObj.add(emitter.rootObj);
            } else {
                effect.rootObj.add(emitter.rootObj);
            }
        }

        if (callback) {
            callback();
        }
    });

    return effect;
};

EffectManager._loadMeshAnimation = function (data: any, callback: any) {
    let animation = new EffectAnimation();
    const texture = TextureManager.load(data.texturePath);

    // Set animation properties
    animation.name = data.name;

    // Create material
    let material = ShaderManager.get("partmesh").clone();

    material.uniforms = {
        texture1: { type: "t", value: texture },
        dayNightCycle: { type: "f", value: 0.0 },
    };

    material.transparent = true;
    material.depthTest = data.depthTestEnabled;
    material.depthWrite = data.depthWriteEnabled;
    material.blending = THREE.CustomBlending;
    material.blendEquation = convertZnzinBlendOp(data.blendOp);
    material.blendSrc = convertZnzinBlendType(data.blendSrc);
    material.blendDst = convertZnzinBlendType(data.blendDst);

    if (data.twoSideEnabled) {
        material.side = THREE.DoubleSide;
    }

    if (data.alphaEnabled) {
        if (data.alphaTestEnabled) {
            material.alphaTest = 0.5;
        }
    }

    animation.material = material;

    Mesh.load(data.meshPath, (geometry: any) => {
        animation.mesh = new THREE.Mesh(geometry, animation.material);
        animation.rootObj.add(animation.mesh);

        // ROSE, you suck...
        if (data.animationPath) {
            AnimationData.load(data.animationPath, (animData: any) => {
                const meshAnim = new GeometryAnimator(geometry, animData);
                meshAnim.loop = data.loopCount;
                animation.meshAnimation = meshAnim;
                callback();
            });
        } else {
            callback();
        }
    });

    return animation;
};

EffectManager._createParticleEffect = function (data: any, callback: any) {
    let waitAll = new MultiWait();

    let particle = EffectManager._loadParticleEffect(
        data.particlePath,
        waitAll.one()
    );

    // Set initial properties
    particle.startDelay = data.delay / 1000;
    particle.rootObj.position.copy(data.position);
    particle.rootObj.quaternion.copy(data.rotation);

    if (data.animation.enabled) {
        particle.subRootObj = particle.rootObj;
        particle.rootObj = new THREE.Object3D();
        particle.rootObj.add(particle.subRootObj);

        AnimationData.load(
            data.animation.path,
            function (particleAnimWait: any, animData: any) {
                particle.animation = new ObjectAnimator(
                    particle.rootObj,
                    animData
                );
                particle.animation.loop = data.animation.loopCount;
                particleAnimWait();
            }.bind(this, waitAll.one())
        );
    }

    waitAll.wait(callback);

    return particle;
};

EffectManager._createMeshAnimation = function (data: any, callback: any) {
    let waitAll = new MultiWait();

    let meshAnimation = EffectManager._loadMeshAnimation(data, waitAll.one());

    // Set initial properties
    meshAnimation.startDelay = data.delay / 1000;
    meshAnimation.rootObj.position.copy(data.position);
    meshAnimation.rootObj.quaternion.copy(data.rotation);

    if (data.animation.enabled) {
        meshAnimation.subRootObj = meshAnimation.rootObj;
        meshAnimation.rootObj = new THREE.Object3D();
        meshAnimation.rootObj.add(meshAnimation.subRootObj);

        AnimationData.load(
            data.animation.path,
            function (meshAnimWait: any, animData: any) {
                meshAnimation.animation = new ObjectAnimator(
                    meshAnimation.rootObj,
                    animData
                );
                meshAnimation.loop = data.animation.loopCount;
                meshAnimWait();
            }.bind(this, waitAll.one())
        );
    }

    waitAll.wait(callback);

    return meshAnimation;
};

EffectManager.loadEffectByIdx = function (index: any, callback: any) {
    let effect = new Effect();

    GDM.get("file_effect", (effectFileTable: any) => {
        const effectRow = effectFileTable.row(index);
        EffectManager.loadEffect(effectRow[1], callback, effect);
    });

    return effect;
};

EffectManager.loadEffect = function (path: any, callback: any, effect?: any) {
    // config.noeffects = true;
    if (config.noeffects) {
        // Don't load effects if they are disabled by configuration.
        callback(null);
        return;
    }

    effect = effect || new Effect();

    EffectData.load(path, (effectData: any) => {
        const waitAll = new MultiWait();

        effect.path = path;
        effect.rootObj.name = effectData.name + ".rootObj";
        effect.rootObj2.name = effectData.name + ".rootObj2";
        // TODO: Effect sound - data.soundEnabled, data.soundPath

        for (let i = 0; i < effectData.particles.length; ++i) {
            let data = effectData.particles[i];
            const particle = EffectManager._createParticleEffect(
                data,
                waitAll.one()
            );

            if (data.linkRoot) {
                effect.rootObj.add(particle.rootObj);
            } else {
                effect.rootObj2.add(particle.rootObj);
            }

            effect.particleEffects.push(particle);
        }

        for (let i = 0; i < effectData.animations.length; ++i) {
            const data = effectData.animations[i];
            const meshAnimation = EffectManager._createMeshAnimation(
                data,
                waitAll.one()
            );

            if (data.linkRoot) {
                effect.rootObj.add(meshAnimation.rootObj);
            } else {
                effect.rootObj2.add(meshAnimation.rootObj);
            }

            effect.animations.push(meshAnimation);
        }

        waitAll.wait(() => {
            callback(effect);
        });
    });
};

export function playSkillCastEffect(pawn: any, skillData: any): void {
    const effectIdx = Number(skillData?.[SKILL.CASTING_EFFECT1]);
    if (!effectIdx) return;

    EffectManager.loadEffectByIdx(effectIdx, (effect: any) => {
        if (effect && pawn?.rootObj) {
            pawn.rootObj.add(effect.rootObj);
            effect.play();
        }
    });
}

export function attachBuffEffect(pawn: any, effectFileIdx: number): { remove: () => void } {
    let effect: any = null;
    let removed = false;

    if (effectFileIdx) {
        EffectManager.loadEffectByIdx(effectFileIdx, (loadedEffect: any) => {
            if (loadedEffect && pawn?.rootObj && !removed) {
                pawn.rootObj.add(loadedEffect.rootObj);
                loadedEffect.play();
                effect = loadedEffect;
            }
        });
    }

    return {
        remove(): void {
            removed = true;
            if (effect) {
                effect.stop();
                pawn.rootObj.remove(effect.rootObj);
                effect = null;
            }
        },
    };
}

export { EffectManager };
